import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, ArrowLeft, Bookmark, Info } from 'lucide-react';

// All 7 quiz questions from the Free Fit Quiz Platform Spec
const QUESTIONS = [
  {
    id: 'q1',
    text: 'What brings you here today?',
    options: [
      "I've been made redundant and I'm figuring out what's next",
      "I'm employed but I can't stop thinking about doing something of my own",
      "I want to build a business but I don't know where to start",
      "I've had an idea for a while and I'm finally ready to do something about it",
      "I'm not sure yet — I'm just exploring",
    ],
  },
  {
    id: 'q2',
    text: 'How long have you been in your career or profession?',
    options: [
      'Under 3 years',
      '3–7 years',
      '8–15 years',
      '15+ years',
    ],
  },
  {
    id: 'q3',
    text: 'When you imagine your ideal working life in 2–3 years, which feels closest?',
    options: [
      'Running my own business — even a small one',
      "Being in the right employed role, doing work I'm genuinely good at",
      "I'm genuinely not sure yet — I need to figure that out first",
      'Something in between — maybe a side business alongside work',
    ],
  },
  {
    id: 'q4',
    text: 'Have you ever taken action on a business idea — even informally?',
    options: [
      "Yes — I've done something, even if it was small or didn't last",
      "I've thought about it seriously but haven't started",
      "Not really — I wouldn't know where to begin",
      "No, and I'm not sure I want to",
    ],
  },
  {
    id: 'q5',
    text: 'What feels like your biggest barrier right now?',
    options: [
      "I don't know what kind of business I could build",
      "I have an idea but I don't know if it's viable",
      "I'm not sure I have what it takes to run a business",
      "Money and financial security — I can't afford to get this wrong",
      "I just need a structured process — I don't know the steps",
    ],
  },
  {
    id: 'q6',
    text: 'What would make the next 12 months feel like a success?',
    options: [
      "I've launched something — even small — and it's generating income",
      "I'm in the right role, finally being paid what I'm worth",
      "I've figured out my direction and I have a clear plan",
      "I've tested my idea and I know whether to pursue it or not",
    ],
  },
  {
    id: 'q7',
    text: 'One honest question: are you ready to do real work on this?',
    subtitle: "SPRING is not a course you watch. It's a programme you complete.",
    options: [
      "Yes — I'm ready to commit time and effort to this",
      'Probably — if the structure is clear and the path makes sense',
      "I'm not sure — I want to know more before I commit",
      "Honestly, not yet — but I'm interested for the future",
    ],
  },
];

// Personalisation map for Q6 answer → result screen secondary line
const Q6_PERSONALISATION = {
  0: "Stage 1 is where that launch begins.",
  1: "Stage 1 helps you find the right direction — whether that's building or landing.",
  2: "Stage 1 is where that direction gets found.",
  3: "Stage 1 is where you test your idea before you invest in it.",
};

const FitQuiz = () => {
  const navigate = useNavigate();
  const [screen, setScreen] = useState('welcome'); // welcome | quiz | result-standard | result-alternate
  const [currentQ, setCurrentQ] = useState(0);
  const [answers, setAnswers] = useState({});
  const [selectedOption, setSelectedOption] = useState(null);

  const progress = screen === 'welcome' ? 0 : screen === 'quiz' ? Math.round(((currentQ) / QUESTIONS.length) * 100) : 100;

  const handleStart = () => {
    setScreen('quiz');
    setCurrentQ(0);
  };

  const handleSelectOption = (optionIndex) => {
    setSelectedOption(optionIndex);
  };

  const handleNext = () => {
    if (selectedOption === null) return;

    const newAnswers = { ...answers, [QUESTIONS[currentQ].id]: selectedOption };
    setAnswers(newAnswers);
    setSelectedOption(null);

    if (currentQ < QUESTIONS.length - 1) {
      setCurrentQ(currentQ + 1);
    } else {
      // Q7 answer 4 (index 3) = "Honestly, not yet" → alternate result
      if (newAnswers.q7 === 3) {
        setScreen('result-alternate');
      } else {
        setScreen('result-standard');
      }
    }
  };

  const handleBack = () => {
    if (currentQ > 0) {
      setCurrentQ(currentQ - 1);
      setSelectedOption(answers[QUESTIONS[currentQ - 1].id] ?? null);
    } else {
      setScreen('welcome');
    }
  };

  const handleSavePage = () => {
    // Trigger bookmark prompt
    if (window.sidebar && window.sidebar.addPanel) {
      window.sidebar.addPanel(document.title, window.location.href, '');
    } else {
      alert('Press Ctrl+D (or Cmd+D on Mac) to bookmark this page. SPRING will be here when you\'re ready.');
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: '#F6ADBC',
      fontFamily: 'Arial, sans-serif',
      display: 'flex',
      flexDirection: 'column',
    }}>
      {/* Header */}
      <header style={{
        padding: '16px 24px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        borderBottom: '1px solid rgba(0,0,0,0.08)',
      }}>
        <div>
          <span
            style={{ fontSize: '22px', fontWeight: 800, color: '#1E491A', letterSpacing: '-0.5px', cursor: 'pointer' }}
            onClick={() => navigate('/')}
            data-testid="quiz-logo"
          >
            SPRING.
          </span>
          <span style={{ fontSize: '11px', color: '#1E491A', opacity: 0.5, marginLeft: '8px', letterSpacing: '0.5px' }}>
          </span>
        </div>
        {screen !== 'welcome' && (
          <span style={{ fontSize: '13px', color: '#1E491A', opacity: 0.6 }}>
            {screen === 'quiz' ? `${currentQ + 1} of ${QUESTIONS.length}` : 'Complete'}
          </span>
        )}
      </header>

      {/* Progress Bar */}
      <div style={{ height: '3px', background: 'rgba(0,0,0,0.08)' }}>
        <div
          style={{
            height: '100%',
            width: `${progress}%`,
            background: '#1a1a1a',
            transition: 'width 0.4s ease',
          }}
          data-testid="quiz-progress-bar"
        />
      </div>

      {/* Main Content */}
      <main style={{
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '40px 24px',
      }}>
        <div style={{ maxWidth: '620px', width: '100%' }}>

          {/* ============= WELCOME SCREEN ============= */}
          {screen === 'welcome' && (
            <div style={{ textAlign: 'center' }} data-testid="quiz-welcome-screen">
              <h1 style={{
                fontSize: 'clamp(28px, 4vw, 42px)',
                fontWeight: 800,
                color: '#1E491A',
                letterSpacing: '-1px',
                lineHeight: 1.1,
                marginBottom: '16px',
              }}
              data-testid="quiz-welcome-heading"
              >
                Is SPRING right for you right now?
              </h1>
              <p style={{
                fontSize: '15px',
                color: '#1E491A',
                opacity: 0.7,
                marginBottom: '32px',
                fontWeight: 500,
              }}>
                7 questions. Less than 3 minutes. No sign-up.
              </p>
              <p style={{
                fontSize: '15px',
                color: '#1E491A',
                opacity: 0.75,
                lineHeight: 1.7,
                maxWidth: '500px',
                margin: '0 auto 40px',
              }}>
                SPRING is New Zealand's guided platform for career transitioners — people who are figuring out what's next, whether that means building a business, finding the right role, or getting clear on their direction. This quiz helps us confirm you're in the right place.
              </p>
              <p style={{
                fontSize: '14px',
                color: '#1E491A',
                opacity: 0.6,
                fontStyle: 'italic',
                marginBottom: '36px',
              }}>
                There are no wrong answers.
              </p>
              <button
                onClick={handleStart}
                data-testid="quiz-start-btn"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '10px',
                  background: '#1a1a1a',
                  color: '#F6ADBC',
                  border: 'none',
                  padding: '16px 36px',
                  borderRadius: 0,
                  fontSize: '16px',
                  fontWeight: 700,
                  cursor: 'pointer',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                }}
                onMouseEnter={(e) => { e.target.style.transform = 'translateY(-2px)'; e.target.style.boxShadow = '0 8px 24px rgba(0,0,0,0.2)'; }}
                onMouseLeave={(e) => { e.target.style.transform = ''; e.target.style.boxShadow = ''; }}
              >
                Start the Quiz
                <ArrowRight style={{ width: 18, height: 18 }} />
              </button>
            </div>
          )}

          {/* ============= QUESTION SCREEN ============= */}
          {screen === 'quiz' && (
            <div data-testid={`quiz-question-${currentQ + 1}`}>
              <button
                onClick={handleBack}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  background: 'none',
                  border: 'none',
                  fontSize: '14px',
                  color: '#1E491A',
                  opacity: 0.5,
                  cursor: 'pointer',
                  marginBottom: '24px',
                  padding: 0,
                }}
                data-testid="quiz-back-btn"
              >
                <ArrowLeft style={{ width: 14, height: 14 }} />
                Back
              </button>

              <h2 style={{
                fontSize: 'clamp(22px, 3vw, 32px)',
                fontWeight: 700,
                color: '#1E491A',
                lineHeight: 1.2,
                marginBottom: QUESTIONS[currentQ].subtitle ? '10px' : '32px',
                letterSpacing: '-0.5px',
              }}>
                {QUESTIONS[currentQ].text}
              </h2>

              {QUESTIONS[currentQ].subtitle && (
                <p style={{
                  fontSize: '14px',
                  color: '#1E491A',
                  opacity: 0.6,
                  fontStyle: 'italic',
                  marginBottom: '32px',
                }}>
                  {QUESTIONS[currentQ].subtitle}
                </p>
              )}

              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {QUESTIONS[currentQ].options.map((option, idx) => (
                  <button
                    key={idx}
                    onClick={() => handleSelectOption(idx)}
                    data-testid={`quiz-option-${idx}`}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '14px',
                      padding: '16px 20px',
                      borderRadius: '14px',
                      border: selectedOption === idx ? '2px solid #1a1a1a' : '2px solid rgba(0,0,0,0.1)',
                      background: selectedOption === idx ? '#1a1a1a' : 'rgba(255,255,255,0.6)',
                      color: selectedOption === idx ? '#F6ADBC' : '#1a1a1a',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      textAlign: 'left',
                      fontSize: '15px',
                      fontWeight: selectedOption === idx ? 600 : 400,
                      lineHeight: 1.4,
                    }}
                  >
                    <div style={{
                      width: '20px',
                      height: '20px',
                      borderRadius: '50%',
                      border: selectedOption === idx ? '6px solid #F6ADBC' : '2px solid rgba(0,0,0,0.25)',
                      flexShrink: 0,
                      transition: 'all 0.2s',
                      background: selectedOption === idx ? 'transparent' : 'transparent',
                    }} />
                    {option}
                  </button>
                ))}
              </div>

              <button
                onClick={handleNext}
                disabled={selectedOption === null}
                data-testid="quiz-next-btn"
                style={{
                  marginTop: '28px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  background: selectedOption !== null ? '#1a1a1a' : 'rgba(0,0,0,0.15)',
                  color: selectedOption !== null ? '#F6ADBC' : 'rgba(0,0,0,0.3)',
                  border: 'none',
                  padding: '14px 32px',
                  borderRadius: 0,
                  fontSize: '15px',
                  fontWeight: 600,
                  cursor: selectedOption !== null ? 'pointer' : 'not-allowed',
                  transition: 'all 0.2s',
                }}
              >
                {currentQ === QUESTIONS.length - 1 ? 'See My Result' : 'Continue'}
                <ArrowRight style={{ width: 16, height: 16 }} />
              </button>
            </div>
          )}

          {/* ============= STANDARD RESULT SCREEN ============= */}
          {screen === 'result-standard' && (
            <div data-testid="quiz-result-standard">
              <div style={{
                textAlign: 'center',
                marginBottom: '40px',
              }}>
                <div style={{
                  display: 'inline-block',
                  background: '#1a1a1a',
                  color: '#F6ADBC',
                  padding: '6px 18px',
                  borderRadius: 0,
                  fontSize: '12px',
                  fontWeight: 700,
                  letterSpacing: '1px',
                  marginBottom: '20px',
                  textTransform: 'uppercase',
                }}>
                  Quiz Complete
                </div>
                <h1 style={{
                  fontSize: 'clamp(28px, 4vw, 40px)',
                  fontWeight: 800,
                  color: '#1E491A',
                  letterSpacing: '-1px',
                  lineHeight: 1.1,
                  marginBottom: '20px',
                }}
                data-testid="quiz-result-heading"
                >
                  You are a SPRING candidate.
                </h1>
              </div>

              <div style={{
                background: 'rgba(255,255,255,0.7)',
                borderRadius: 0,
                padding: '28px',
                marginBottom: '28px',
                lineHeight: 1.7,
              }}>
                <p style={{ fontSize: '15px', color: '#1E491A', marginBottom: '16px' }}>
                  SPRING is built for exactly the kind of person who just answered those questions. Someone with real professional experience. A genuine question about what's next. And the self-awareness to take that question seriously.
                </p>
                <p style={{ fontSize: '15px', color: '#1E491A', marginBottom: '0' }}>
                  What SPRING does is take you from where you are right now to a clear, specific direction — with a structured process, no guesswork, and no wasted time.
                </p>
              </div>

              <div style={{
                background: 'rgba(255,255,255,0.5)',
                borderRadius: 0,
                padding: '24px 28px',
                marginBottom: '28px',
              }}>
                <p style={{ fontSize: '14px', color: '#1E491A', opacity: 0.6, marginBottom: '14px', fontWeight: 600 }}>
                  Inside the platform, you'll be placed on one of three pathways:
                </p>
                {[
                  { label: 'Pathway A: Founders Track', desc: 'for those ready to build a business now' },
                  { label: 'Pathway B: Employment Track', desc: 'for those whose strengths point to the right employed role' },
                  { label: 'Pathway C: Mindset Discovery', desc: 'for those who need to build the foundations first' },
                ].map((p, i) => (
                  <div key={i} style={{ display: 'flex', gap: '10px', marginBottom: '8px', alignItems: 'flex-start' }}>
                    <span style={{ color: '#1E491A', fontWeight: 700, fontSize: '14px', flexShrink: 0 }}>→</span>
                    <p style={{ fontSize: '14px', color: '#1E491A', margin: 0 }}>
                      <strong>{p.label}</strong> — {p.desc}
                    </p>
                  </div>
                ))}
                <p style={{ fontSize: '13px', color: '#1E491A', opacity: 0.55, marginTop: '14px', fontStyle: 'italic' }}>
                  No pathway is better than another. Each one is the right answer for a different person at a different moment.
                </p>
              </div>

              <p style={{ fontSize: '15px', color: '#1E491A', opacity: 0.8, marginBottom: '24px', textAlign: 'center' }}>
                The full Stage 1 Assessment takes 45–90 minutes and tells you exactly which pathway is yours.
              </p>

              <div style={{ textAlign: 'center' }}>
                <button
                  onClick={() => navigate('/auth')}
                  data-testid="quiz-begin-stage1-btn"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '10px',
                    background: '#1a1a1a',
                    color: '#F6ADBC',
                    border: 'none',
                    padding: '16px 36px',
                    borderRadius: 0,
                    fontSize: '16px',
                    fontWeight: 700,
                    cursor: 'pointer',
                    transition: 'transform 0.2s, box-shadow 0.2s',
                  }}
                  onMouseEnter={(e) => { e.target.style.transform = 'translateY(-2px)'; e.target.style.boxShadow = '0 8px 24px rgba(0,0,0,0.2)'; }}
                  onMouseLeave={(e) => { e.target.style.transform = ''; e.target.style.boxShadow = ''; }}
                >
                  Begin Your Stage 1 Assessment
                  <ArrowRight style={{ width: 18, height: 18 }} />
                </button>

                {/* Personalised secondary line from Q6 */}
                {answers.q6 !== undefined && Q6_PERSONALISATION[answers.q6] && (
                  <p style={{
                    fontSize: '13px',
                    color: '#1E491A',
                    opacity: 0.55,
                    marginTop: '16px',
                    fontStyle: 'italic',
                  }}
                  data-testid="quiz-q6-personalisation"
                  >
                    {Q6_PERSONALISATION[answers.q6]}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* ============= ALTERNATE RESULT SCREEN (NOT YET) ============= */}
          {screen === 'result-alternate' && (
            <div data-testid="quiz-result-alternate">
              <div style={{ textAlign: 'center', marginBottom: '32px' }}>
                <div style={{
                  display: 'inline-block',
                  background: 'rgba(0,0,0,0.08)',
                  color: '#1E491A',
                  padding: '6px 18px',
                  borderRadius: 0,
                  fontSize: '12px',
                  fontWeight: 700,
                  letterSpacing: '1px',
                  marginBottom: '20px',
                  textTransform: 'uppercase',
                }}>
                  Quiz Complete
                </div>
              </div>

              <div style={{
                background: 'rgba(255,255,255,0.7)',
                borderRadius: 0,
                padding: '32px',
                lineHeight: 1.8,
                marginBottom: '32px',
              }}>
                <h2 style={{
                  fontSize: '24px',
                  fontWeight: 700,
                  color: '#1E491A',
                  marginBottom: '16px',
                  lineHeight: 1.3,
                }}
                data-testid="quiz-alternate-heading"
                >
                  That's an honest answer. And honesty is exactly what SPRING runs on.
                </h2>
                <p style={{ fontSize: '15px', color: '#1E491A', marginBottom: '16px' }}>
                  You're not ready to commit right now — and that's a completely valid place to be. Forcing a process before you're ready doesn't work. SPRING knows this.
                </p>
                <p style={{ fontSize: '15px', color: '#1E491A', marginBottom: '0' }}>
                  When the time is right — when the question gets louder, or circumstances change, or you're simply ready to find out what's possible — SPRING will be here. Bookmark this page. Come back when you're ready. There's no expiry on that.
                </p>
              </div>

              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
                alignItems: 'center',
              }}>
                <button
                  onClick={handleSavePage}
                  data-testid="quiz-save-page-btn"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '10px',
                    background: '#1a1a1a',
                    color: '#F6ADBC',
                    border: 'none',
                    padding: '14px 32px',
                    borderRadius: 0,
                    fontSize: '15px',
                    fontWeight: 600,
                    cursor: 'pointer',
                    transition: 'transform 0.2s',
                  }}
                >
                  <Bookmark style={{ width: 16, height: 16 }} />
                  Save This Page
                </button>
                <button
                  onClick={() => navigate('/')}
                  data-testid="quiz-learn-more-btn"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                    background: 'rgba(255,255,255,0.6)',
                    color: '#1E491A',
                    border: '2px solid rgba(0,0,0,0.1)',
                    padding: '14px 32px',
                    borderRadius: 0,
                    fontSize: '15px',
                    fontWeight: 600,
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                  }}
                >
                  <Info style={{ width: 16, height: 16 }} />
                  Tell Me More About SPRING First
                </button>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default FitQuiz;
